/**
 * Helper functions for reloadPositions.ts.
 */

import * as babylon from "@babylonjs/core";

/**
 * Projects geodetic lat/lon (deg) and alt (m) to east/north/up meters.
 *
 * See the algorithm here:
 * https://en.wikipedia.org/wiki/Geographic_coordinate_conversion#From_geodetic_to_ECEF_coordinates
 *
 * NOTE:
 * The wikipedia article works with z-up coordinates,
 * but babylon uses y-up coordinates under the hood.
 * So this implementation transposes the inputs and outputs.
 * y-up data comes in, y-up data goes out, but the math is done
 * with z-up for easier comparison to the ellipsoidal projection algorithms.
 */
export function projectLLAToENU(
  point: babylon.Vector3,
  /** The lla coordinates to use as the origin of enu space */
  origin: babylon.Vector3,
): babylon.Vector3 {
  const lla: [number, number, number] = [point.x, point.z, point.y];
  const llaOrigin: [number, number, number] = [origin.x, origin.z, origin.y];
  const ecef = llaToEcef(lla);
  const ecefOrigin = llaToEcef(llaOrigin);
  const enu = ecefToEnu(ecef, ecefOrigin, llaOrigin);
  return new babylon.Vector3(enu[0], enu[2], enu[1]);
}

/**
 * Projects the ellipsoidal lat/lon/alt coordinates into earth-centered-earth-fixed coordinates.
 *
 * This is a transformation from radial to cartesian coordinates, with two nuances:
 *
 * 3D instead of 2D:
 *   * x = r * cos(lat) * cos(lon)
 *   * y = r * cos(lat) * sin(lon)
 *   * z = r * sin(lat)
 *
 * Ellipsoidal deformation (flattening) along the longitude axis
 *
 */
function llaToEcef([lat, lon, alt]: [number, number, number]): [
  number,
  number,
  number,
] {
  // TODO: if a customer wanted it, we could swap out the ellipsoid for a Moon or Mars one.
  const { a, e2 } = WGS84;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const cosLon = Math.cos((lon * Math.PI) / 180);
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const sin2Lat = sinLat ** 2;
  const sinLon = Math.sin((lon * Math.PI) / 180);

  // nPhi is the prime vertical radius of curvature
  // That is, the distance to the surface of the ellipsoid at the given lat/lon,
  // from the semi-minor axis, passing through the semi-major axis.
  // const nPhi = (a * a) / Math.sqrt(a * a * cos2Lat + b * b * sin2Lat);
  const nPhi = a / Math.sqrt(1 - e2 * sin2Lat);
  // It gives us a constant radius for the earth at a given line of latitude.
  const r = nPhi + alt;
  // X and Y are points on a plane, displaced from a constant-radius circle of latitude
  // by the altitude.
  // That circle's radius is given by r*cos(lat).
  // Then the longitude factors into x and y as a basic 2D polar -> cartesian projection.
  const x = r * cosLat * cosLon;
  const y = r * cosLat * sinLon;
  // Z is a point along the ellipse of the current line of longitude.
  // The factor (b*b)/(a*a) is the deformation of the ellipse from a perfect circle.
  const deformation = 1 - e2;
  // After applying the flattening, the rest is analagous to the r * sin(lon) used in 2D polar->cartesian projection.
  const z = (deformation * nPhi + alt) * sinLat;
  return [x, y, z];
}

/**
 * Project the origin lat/lon into ENU space.
 *
 * This is a rotation transformation such that up is aligned with the tangent plane
 * to the earth (away from the center) at the given LLA Origin position.
 *
 * Before this transformation, up is instead aligned with the earth's axis of rotation.
 */
function ecefToEnu(
  ecef: [number, number, number],
  ecefOrigin: [number, number, number],
  llaOrigin: [number, number, number],
): [number, number, number] {
  // Compute the offset of the given point relative to the ecef origin point.
  const dx0 = ecef[0] - ecefOrigin[0];
  const dy0 = ecef[1] - ecefOrigin[1];
  const dz0 = ecef[2] - ecefOrigin[2];

  const [lat0, lon0] = llaOrigin;
  const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
  const cosLon0 = Math.cos((lon0 * Math.PI) / 180);
  const sinLat0 = Math.sin((lat0 * Math.PI) / 180);
  const sinLon0 = Math.sin((lon0 * Math.PI) / 180);

  // Matrix multiplication!
  // https://en.wikipedia.org/wiki/Geographic_coordinate_conversion#From_ECEF_to_ENU
  const x = -sinLon0 * dx0 + cosLon0 * dy0;
  const y = -sinLat0 * cosLon0 * dx0 + -sinLat0 * sinLon0 * dy0 + cosLat0 * dz0;
  const z = cosLat0 * cosLon0 * dx0 + cosLat0 * sinLon0 * dy0 + sinLat0 * dz0;
  // return ecef;
  return [x, y, z];
}

/**
 * The Earth's ellipsoidal shape, as defined by WGS84, which is the survey used by the GPS system.
 *
 * Sourced from https://en.wikipedia.org/wiki/World_Geodetic_System#WGS_84
 */
const WGS84 = {
  /**
   * Semi-major axis in meters of the wgs84 chubby earth ellipsoid (the radius of the earth at its equator).
   *
   * "Semi-major axis" means half of the length of the larger axis across the ellipsoid.
   */
  a: 6378137.0,

  /**
   * Semi-minor axis in meters of the wgs84 chubby earth ellipsoid.
   *
   * The semi-minor axis is the radius of the earth from the core to the north or south pole.
   * "Semi-minor axis" means half the length of the smaller axis across the ellipsoid.
   *
   * Conceptually, a and b are the parameters of the ellipse, but in practice,
   * a and the inverse flattening factor 1/f are the parameters of the WGS84 ellipsoid.
   *
   * b is calculated from a and f as b = a * (1-f).
   *
   * This value is the approximation of b given by the wikipedia article.
   */
  b: 6356752.314245,

  /**
   * The inverse of the flattening factor.
   *
   * The flattening factor f = 1 - (b/a), so it is a unit-less scalar.
   *
   * The inverse, then is 1/f.
   */
  inverseF: 298.257223563,

  /**
   * The numerical eccentricity, 1 - (b**2)/(a**2), which is a unit-less scalar.
   *
   * To get a higher-precision projection than what I could find online, I computed this value
   * with Python's Decimal type with `decima.getcontext().prec = 256`,
   * then truncated the result to fit in the float64 number type used by JavaScript.
   *
   * ```
   * >>> f_inv = Decimal('298.257223563')
   * >>> f = 1 / f_inv
   * >>> a = Decimal("6378137")
   * >>> b = a * (1-f)
   * >>> e2 = b/a * b/a
   * ```
   *
   * Using this approach helped me raise the precision on our coordinate projection from centimeters to nanometers
   * in the unit tests.
   *
   * Using a precise e2 value instead of f or b values was the easiest way to minimize the scale-changing computations
   * (sqrt, exponent, multiplication, division) that drastically effect precision of the calculation.
   *
   * cos/sin computations generally don't lose as much precision because their values stay between -1 and 1.
   */
  e2: 0.006694379990141317,
} as const;
